// /src/api/ImovelExecutionEngine.js
// VERSÃO 44.2 SUPREMA - PRECISION FINANCE & DATA POWER (TIM UNITY)

import apiClient from './apiClient';
import { toast } from 'react-toastify';
import confetti from 'canvas-confetti';
import React from 'react';

// Auxiliar Interno de Purificação Financeira (Garante centavos e remove lixo de máscara)
const parseCurrency = (val) => {
    if (!val && val !== 0) return 0;
    if (typeof val === 'number') return val;
    // Remove R$, espaços e pontos de milhar, troca vírgula decimal por ponto
    const cleanString = String(val)
        .replace(/[R$\s]/g, '')
        .replace(/\./g, '')
        .replace(',', '.');
    return parseFloat(cleanString) || 0;
};

export const ImovelExecutionEngine = {

    /* ==========================================================
        ⚔️ XERIFE DE REQUISITOS (VALIDAÇÃO DE ELITE)
    ========================================================== */
    validarFaseFinal(formData) {
        const regras = [
            { field: 'titulo', label: 'Nome Comercial do Plano', secao: 'Identificação' },
            { field: 'quantidadegb', label: 'Quantidade de GB (Franquia)', secao: 'Identificação' },
            { field: 'codigoImovel', label: 'Referência SKU', secao: 'Identificação' },
            { field: 'tipoNegocio', label: 'Tipo de Oferta', secao: 'Identificação' },
            { field: 'tipoPrincipal', label: 'Público Alvo (CPF/CNPJ)', secao: 'Venda' },
            { field: 'subtipo', label: 'Tipo de Ativação/Entrega', secao: 'Venda' },
            { field: 'descricao', label: 'Narrativa Comercial', secao: 'Narrativa', minLength: 10 }
        ];

        const erros = [];
        
        // 🛰️ Validação de Abrangência Multi-Radar
        if (formData.nivelCobertura === 'Estados' && (!formData.estadosCobertura || formData.estadosCobertura.length === 0)) {
            erros.push({ label: 'Selecione ao menos um Estado', secao: 'Localização' });
        }
        
        if (formData.nivelCobertura === 'Cidades' && (!formData.locaisCirurgicos || formData.locaisCirurgicos.length === 0)) {
            erros.push({ label: 'Injete ao menos um CEP no Radar', secao: 'Localização' });
        }

        regras.forEach(regra => {
            const value = formData[regra.field];
            const vazio = value === null || value === undefined || value === '' || (typeof value === 'number' && value === 0);

            if (vazio) { erros.push(regra); return; }
            if (regra.minLength && typeof value === 'string' && value.trim().length < regra.minLength) { erros.push(regra); return; }
        });

        // 💰 Validação Financeira com Precisão de Centavos
        const valorVendaNum = parseCurrency(formData.valorVenda);

        if (valorVendaNum <= 0) {
            erros.push({ label: 'Mensalidade do Plano (Mínimo R$ 0,01)', secao: 'Financeiro' });
        }

        if (erros.length > 0) {
            toast.error(
                <div className="p-1">
                    <strong className="text-sm uppercase tracking-tighter">🚨 Plano com Pendências</strong>
                    <ul className="mt-3 space-y-1 bg-black/20 p-3 rounded-xl border border-red-500/20">
                        {erros.map((e, i) => (
                            <li key={i} className="text-[9px] font-bold text-white uppercase flex items-center gap-2">
                                <div className="h-1 w-1 bg-red-500 rounded-full animate-pulse" /> {e.label} <span className="opacity-40">[{e.secao}]</span>
                            </li>
                        ))}
                    </ul>
                </div>, { autoClose: 8000, position: "top-center" }
            );
            return false;
        }
        return true;
    },

    /* ==========================================================
        🔄 TRADUTOR TÁTICO: PAYLOAD ESTRUTURADO (FORMDATA)
    ========================================================== */
    prepararPayload(formData, removedImages = []) {
        const payload = new FormData();

        // --- 1. DADOS PRIMITIVOS ---
        const primitives = {
            titulo: formData.titulo?.trim(),
            descricao: formData.descricao?.trim() || '',
            tipoNegocio: formData.tipoNegocio,
            tipoPrincipal: formData.tipoPrincipal,
            subtipo: formData.subtipo,
            codigoImovel: String(formData.codigoImovel || '').trim(),
            quantidadegb: Number(formData.quantidadegb || 0),
            nomeImobiliaria: formData.nomeImobiliaria || 'Unity - Contabilizoom',
            creci: String(formData.creci || '').trim(),
            entidadeId: String(formData.entidadeId),
            usuarioId: String(formData.usuarioId),
            isDestaque: formData.isDestaque === true ? 'true' : 'false',
            seloDestaque: formData.seloDestaque || '',
            isVIP: formData.isVIP || 'Não',
            isPortabilidade: formData.isPortabilidade || 'Não',
            isClienteTIM_CPF: formData.isClienteTIM_CPF || 'Não',
            beneficio1: formData.beneficio1 || '',
            beneficio2: formData.beneficio2 || '',
            beneficio3: formData.beneficio3 || '',
            beneficio4: formData.beneficio4 || '',
            nivelCobertura: formData.nivelCobertura || 'Brasil',
            videoUrl: formData.videoUrl || '',
            removedImages: JSON.stringify(removedImages)
        };

        Object.entries(primitives).forEach(([key, val]) => payload.append(key, val));

        // --- 2. ESTRUTURAS JSON ---
        const estruturas = {
            estadosCobertura: formData.estadosCobertura || [],
            locaisCirurgicos: formData.locaisCirurgicos || [],
            localizacao: { 
                cep: formData.cep || '', rua: formData.rua || '', numero: formData.numero || '', 
                bairro: formData.bairro || '', cidade: formData.cidade || '', estado: formData.estado || '' 
            },
            caracteristicas: { 
                quartos: String(formData.quartos || 'Não'),
                suites: String(formData.suites || 'Não'),
                banheiros: String(formData.banheiros || 'Não'),
                areaTotal: String(formData.areaTotal || 'Não'),
                vagasGaragem: String(formData.vagasGaragem || ''),
                areaUtil: String(formData.areaUtil || '')
            },
            valores: { 
                venda: parseCurrency(formData.valorVenda), // ✅ Centavos Protegidos
                aluguel: parseCurrency(formData.valorAluguel), 
                condominio: parseCurrency(formData.valorCondominio),
                iptu: String(formData.valorIptu || '')
            },
            seo: { metaTitle: formData.seoTitulo || '', metaDescription: formData.seoDescricao || '' }
        };

        Object.entries(estruturas).forEach(([key, val]) => payload.append(key, JSON.stringify(val)));

        // --- 3. MÍDIAS ---
        const categorias = ['imagensDestaque', 'imagensQuartos', 'imagensBanheiros', 'imagensOutros'];
        categorias.forEach(cat => {
            if (Array.isArray(formData[cat])) {
                formData[cat].forEach(img => {
                    if (img instanceof File) payload.append(cat, img);
                    else if (typeof img === 'string') payload.append(`${cat}_existentes`, img);
                });
            }
        });

        return payload;
    },

    async executarSincronizacao(payload, imovelId = null) {
        try {
            const isEdit = !!imovelId;
            const endpoint = isEdit ? `/api/imoveis/${imovelId}` : '/api/imoveis';
            const method = isEdit ? 'put' : 'post';

            const { data } = await apiClient[method](endpoint, payload, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success(isEdit ? '🛡️ ARSENAL ATUALIZADO!' : '📱 PLANO SINCRONIZADO!');
            confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 }, colors: ['#0000FF', '#EB0404', '#FFFFFF'] });
            return data.success;
        } catch (error) {
            const msg = error.response?.data?.message || error.message;
            toast.error(`❌ FALHA NA SINCRONIZAÇÃO: ${msg}`);
            return false;
        }
    }
};